/**
 * Username validation and formatting utilities
 */

// Reserved usernames that cannot be used
export const RESERVED_USERNAMES = [
  'admin',
  'api',
  'auth',
  'login',
  'register',
  'logout',
  'profile',
  'profiles',
  'dashboard',
  'settings',
  'candidate',
  'candidates',
  'recruiter',
  'recruiters',
  'about',
  'contact',
  'help',
  'support',
  'faq',
  'terms',
  'privacy',
  'pricing',
  'features',
  'blog',
  'careers',
  'team',
  'jobs',
  'public',
  'static',
  'assets',
  'cdn',
  'user',
  'users',
  'account',
  'accounts',
  'test',
  'demo',
  'sample',
  'example',
  'root',
  'system',
  'null',
  'undefined',
]

/**
 * Username validation regex
 * - Lowercase letters (a-z)
 * - Numbers (0-9)
 * - Hyphens (-) allowed between alphanumeric characters
 * - No consecutive hyphens
 * - Cannot start or end with hyphen
 */
const USERNAME_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

export interface UsernameValidationResult {
  valid: boolean
  error?: string
}

/**
 * Validate username format and constraints
 */
export function validateUsername(username: string): UsernameValidationResult {
  if (!username) {
    return { valid: false, error: 'Username is required' }
  }

  // Length check
  if (username.length < 3) {
    return { valid: false, error: 'Username must be at least 3 characters' }
  }

  if (username.length > 30) {
    return { valid: false, error: 'Username must be at most 30 characters' }
  }

  // Format check
  if (!USERNAME_REGEX.test(username)) {
    return {
      valid: false,
      error: 'Username must be lowercase letters, numbers, and hyphens only',
    }
  }

  // Reserved words check
  if (RESERVED_USERNAMES.includes(username.toLowerCase())) {
    return { valid: false, error: 'This username is reserved' }
  }

  return { valid: true }
}

/**
 * Format a string to be a valid username
 * Converts to lowercase, replaces spaces with hyphens, removes invalid characters
 */
export function formatUsername(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/[^a-z0-9-]/g, '') // Remove invalid characters
    .replace(/--+/g, '-') // Replace consecutive hyphens with single hyphen
    .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
    .substring(0, 30) // Limit to 30 characters
}

/**
 * Generate username suggestions from first and last name
 */
export function generateUsernameSuggestions(
  firstName: string,
  lastName: string
): string[] {
  const first = formatUsername(firstName)
  const last = formatUsername(lastName)
  const firstInitial = first.charAt(0)
  const lastInitial = last.charAt(0)

  const suggestions: string[] = []

  // Basic combinations
  if (first && last) {
    suggestions.push(`${first}-${last}`)
    suggestions.push(`${first}${last}`)
    suggestions.push(`${first}-${lastInitial}`)
    suggestions.push(`${firstInitial}-${last}`)
    suggestions.push(`${firstInitial}${last}`)
  } else if (first) {
    suggestions.push(first)
  } else if (last) {
    suggestions.push(last)
  }

  // Remove duplicates and invalid suggestions
  return [...new Set(suggestions)].filter(
    (s) => s.length >= 3 && validateUsername(s).valid
  )
}

/**
 * Check if username is valid (convenience function)
 */
export function isValidUsername(username: string): boolean {
  return validateUsername(username).valid
}
